import { orderDetailsApi } from '../api/orderDetails';

/**
 * Order Details Service - Handles all business logic for order details
 * Separates concerns between API calls (api/) and business logic (services/)
 */
class OrderDetailsService {
  /**
   * Fetch order and customer details
   */
  async fetchOrderDetails(orderId) {
    try {
      const result = await orderDetailsApi.getOrderById(orderId);

      if (result.success) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error || result.message || 'Failed to fetch order',
      };
    } catch (error) {
      console.error('Error in fetchOrderDetails:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch order',
      };
    }
  }

  /**
   * Fetch customer details
   */
  async fetchCustomerDetails(customerId) {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
        };
      }

      const result = await orderDetailsApi.getCustomerDetails(customerId);

      if (result.success) {
        return {
          success: true,
          data: result.data,
        };
      }

      return {
        success: false,
        error: result.error || result.message || 'Failed to fetch customer details',
      };
    } catch (error) {
      console.error('Error in fetchCustomerDetails:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch customer details',
      };
    }
  }

  /**
   * Get order status steps based on delivery method
   */
  getOrderStatusSteps(deliveryMethod = 'ship') {
    if (deliveryMethod === 'pickup') {
      return [
        { status: 'Received', label: 'Order Confirmed' },
        { status: 'Processing', label: 'Order Processing' },
        { status: 'ReadyToShip', label: 'Ready for Pickup' },
        { status: 'Delivered', label: 'Picked Up' },
      ];
    }
    return [
      { status: 'Received', label: 'Order Confirmed' },
      { status: 'Processing', label: 'Order Processing' },
      { status: 'ReadyToShip', label: 'Ready to Ship' },
      { status: 'Shipped', label: 'Shipped' },
      { status: 'Delivered', label: 'Delivered' },
    ];
  }

  /**
   * Human-readable status label, aware of pickup vs ship delivery.
   * Normalizes both the raw enum ("ReadyToShip") and any spaced variant.
   */
  getStatusLabel(status, deliveryMethod = 'ship') {
    if (!status) return 'Pending';
    const key = String(status).replace(/\s+/g, '');
    const labels = deliveryMethod === 'pickup'
      ? { Received: 'Received', Processing: 'Processing', ReadyToShip: 'Ready for Pickup', Shipped: 'Picked Up', Delivered: 'Picked Up', Pending: 'Pending', Cancelled: 'Cancelled' }
      : { Received: 'Received', Processing: 'Processing', ReadyToShip: 'Ready to Ship', Shipped: 'Shipped', Delivered: 'Delivered', Pending: 'Pending', Cancelled: 'Cancelled' };
    return labels[key] || status;
  }

  /**
   * Check if a specific status is complete
   */
  isStatusComplete(currentStatus, checkStatus) {
    const statusOrder = ['Received', 'Processing', 'ReadyToShip', 'Shipped', 'Delivered'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const checkIndex = statusOrder.indexOf(checkStatus);

    return checkIndex <= currentIndex;
  }

  /**
   * Get status color
   */
  getStatusColor(status) {
    const colors = {
      Received: '#707070',
      Processing: '#032541',
      ReadyToShip: '#032541',
      Shipped: '#000000',
      Delivered: '#17ae7b',
      Pending: '#dc3545',
    };
    return colors[status] || '#999999';
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  }

  /**
   * Format date
   */
  formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get delivery method label
   */
  getDeliveryMethodLabel(method) {
    return method === 'ship' ? 'Shipping' : 'Pickup';
  }

  /**
   * Calculate total items count
   */
  calculateTotalItems(items) {
    return items?.reduce((sum, item) => sum + (item?.itemCount || 0), 0) || 0;
  }
}

const orderDetailsService = new OrderDetailsService();
export default orderDetailsService;
